import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = { vapidPublicKey: String };

  // Includes all targets for both the Banner and the Settings Profile page
  static targets = [
    "promptBanner",
    "statusText",
    "statusBadge",
    "subtitle",
    "button",
  ];

  connect() {
    // 1. Immediately sync the UI to reflect the browser's current permission state
    this.checkCurrentPermissionStatus();

    // 2. Hide the prompt banner instantly if they've already made a choice
    if (Notification.permission !== "default" && this.hasPromptBannerTarget) {
      this.promptBannerTarget.classList.add("hidden");
    }

    // 3. If they previously granted permission, silently ensure their subscription is active
    if (Notification.permission === "granted") {
      this.subscribeToPush();
    }
  }

  // ------------------------------------------------------------------------
  // UI STATE MANAGEMENT
  // ------------------------------------------------------------------------

  checkCurrentPermissionStatus() {
    if (!("Notification" in window)) {
      this.applyUIState(
        "Not Supported",
        "bg-[#0D2D2A]/10 text-[#0D2D2A]/40",
        true,
        "Push not supported on this device.",
      );
      return;
    }

    if (Notification.permission === "granted") {
      this.applyUIState(
        "Enabled",
        "bg-[#0A7C6E]/10 text-[#0A7C6E]",
        true,
        "Alerts are active for this device.",
      );
    } else if (Notification.permission === "denied") {
      this.applyUIState(
        "Blocked",
        "bg-[#E47434]/10 text-[#E47434]",
        true,
        "Unblock in your browser settings.",
      );
    } else {
      // Default state: 'default' (unprompted)
      this.applyUIState(
        "Enable",
        "bg-[#0A7C6E] text-white",
        false,
        "Get notified for Daily Drops & Flash Sales",
      );
    }
  }

  applyUIState(badgeText, badgeClasses, disableButton, subtitleText) {
    // Update the visual status pill/badge
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.textContent = badgeText;
      this.statusBadgeTarget.className = `px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-sm transition-colors ${badgeClasses}`;
    }

    // Update the descriptive subtitle text
    if (this.hasSubtitleTarget) {
      this.subtitleTarget.textContent = subtitleText;
    }

    // Disable the button wrapper so they can't spam the prompt if it's already set
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = disableButton;
      if (disableButton) {
        this.buttonTarget.classList.remove(
          "hover:bg-[#0D2D2A]/5",
          "active:bg-[#0D2D2A]/10",
        );
        this.buttonTarget.classList.add("opacity-70", "cursor-default");
      }
    }

    // Legacy support for simple text banners
    if (this.hasStatusTextTarget) {
      if (Notification.permission === "granted") {
        this.statusTextTarget.textContent =
          "Enabled! You will now receive chat alerts.";
      } else if (Notification.permission === "denied") {
        this.statusTextTarget.textContent =
          "Notifications blocked. Unblock in browser settings.";
      }
    }
  }

  // ------------------------------------------------------------------------
  // PERMISSION & SUBSCRIPTION FLOW
  // ------------------------------------------------------------------------

  async requestPermission(event) {
    event.preventDefault();

    // Prevent action if the button is locked
    if (this.hasButtonTarget && this.buttonTarget.disabled) return;

    // INTERCEPT: If they already blocked it, tell them exactly how to fix it!
    if (Notification.permission === "denied") {
      alert(
        "Notifications are blocked! To enable them, click the 🔒 Lock icon in your browser's address bar, turn on Notifications, and refresh the page.",
      );
      return;
    }

    try {
      // Trigger the native browser popup asking for permission
      const permission = await Notification.requestPermission();

      // Instantly recalculate the UI based on what they just clicked
      this.checkCurrentPermissionStatus();

      if (permission === "granted") {
        await this.subscribeToPush();
      }

      if (this.hasPromptBannerTarget) {
        this.promptBannerTarget.classList.add("hidden");
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
    }
  }

  async subscribeToPush() {
    try {
      const registration =
        await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;

      let subscription = await registration.pushManager.getSubscription();

      // If no subscription exists on this device, create a fresh one using your VAPID key
      if (!subscription) {
        const convertedVapidKey = this.urlBase64ToUint8Array(
          this.vapidPublicKeyValue,
        );

        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedVapidKey,
        });
      }

      // Transmit the keys to Rails
      await this.sendSubscriptionToServer(subscription);
    } catch (error) {
      console.error("Failed to subscribe to native push notifications:", error);
    }
  }

  async sendSubscriptionToServer(subscription) {
    // Extract the raw cryptographic data required by the web-push gem
    const p256dh = btoa(
      String.fromCharCode.apply(
        null,
        new Uint8Array(subscription.getKey("p256dh")),
      ),
    );
    const auth = btoa(
      String.fromCharCode.apply(
        null,
        new Uint8Array(subscription.getKey("auth")),
      ),
    );
    const endpoint = subscription.endpoint;

    // Optimization: Skip database hit if we already synced this exact endpoint during this session
    if (sessionStorage.getItem("webpush_synced") === endpoint) return;

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const response = await fetch("/push_subscriptions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({
        push_subscription: { endpoint: endpoint, p256dh: p256dh, auth: auth },
      }),
    });

    if (response.ok) {
      sessionStorage.setItem("webpush_synced", endpoint);
      console.log("✅ Native Web Push keys successfully synced to server.");
    }
  }

  // Utility method required by the browser to convert your VAPID key format
  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, "+")
      .replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}
